import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getCategories, getMenu, createOrder } from '../lib/api';
import { Plus, Minus, Trash2, ShoppingBag, CheckCircle2, Truck, Store, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getWhatsAppLink, generateNewOrderNotification, generateCustomerWhatsAppOrder } from '../components/WhatsAppFloat';

const WA_ICON = <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;

export default function OrderPage() {
  const { t, getLocalizedField } = useLanguage();
  const { items: cartItems, addItem, updateQuantity, removeItem, total, itemCount, clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCheckout, setShowCheckout] = useState(searchParams.get('checkout') === 'true');
  const [fulfillment, setFulfillment] = useState('pickup');
  const [orderForm, setOrderForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', customer_address: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [error, setError] = useState('');
  const [showWhatsAppHint, setShowWhatsAppHint] = useState(false);
  const [whatsAppOpened, setWhatsAppOpened] = useState(false);

  useEffect(() => {
    Promise.all([
      getCategories().then(r => setCategories(r.data)),
      getMenu().then(r => setMenuItems(r.data)),
    ]).catch(console.error);
  }, []);

  useEffect(() => {
    if (searchParams.get('checkout') === 'true' && cartItems.length > 0) setShowCheckout(true);
  }, [searchParams, cartItems.length]);

  const filteredItems = activeCategory === 'all' ? menuItems : menuItems.filter(i => i.category_id === activeCategory);

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setError('');
    if (cartItems.length === 0) { setError(t('cart_empty')); return; }
    if (!orderForm.customer_name || !orderForm.customer_phone) { setError(t('error_fill_fields')); return; }
    if (fulfillment === 'delivery' && !orderForm.customer_address) { setError(t('error_fill_fields')); return; }

    setLoading(true);
    try {
      const orderData = {
        items: cartItems.map(i => ({ item_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        fulfillment,
        customer_name: orderForm.customer_name,
        customer_phone: orderForm.customer_phone,
        customer_email: orderForm.customer_email,
        customer_address: orderForm.customer_address,
        notes: orderForm.notes,
        total
      };
      await createOrder(orderData);

      const placed = {
        items: cartItems.map(i => ({ item_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        fulfillment, customer_name: orderForm.customer_name, customer_phone: orderForm.customer_phone,
        customer_email: orderForm.customer_email, customer_address: orderForm.customer_address,
        total, notes: orderForm.notes
      };
      setPlacedOrder(placed);
      clearCart();

      // Show WhatsApp hint overlay, then auto-open WhatsApp
      setShowWhatsAppHint(true);
      const waMsg = generateCustomerWhatsAppOrder(placed);
      const waUrl = getWhatsAppLink(waMsg);

      setTimeout(() => {
        const opened = window.open(waUrl, '_blank');
        if (opened) {
          setWhatsAppOpened(true);
        }
        // Show success page after short delay
        setTimeout(() => {
          setShowWhatsAppHint(false);
          setOrderSuccess(true);
        }, 1500);
      }, 2000);

    } catch (err) {
      setError(t('error_generic'));
    } finally {
      setLoading(false);
    }
  };

  if (showWhatsAppHint) {
    return (
      <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#25D366]/10 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-10 h-10 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </div>
          <h2 className="font-[family-name:var(--font-display)] text-xl mb-3 text-[hsl(20,17%,12%)]">Bestellung wird an WhatsApp übergeben...</h2>
          <p className="text-sm text-[hsl(22,10%,38%)] mb-4 leading-relaxed">
            Zur finalen Bestätigung wird Ihre Bestellung jetzt an WhatsApp übergeben.<br />
            Bitte senden Sie die Nachricht dort einmal ab.
          </p>
          <div className="w-8 h-8 mx-auto border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
        </motion.div>
      </div>
    );
  }

  if (orderSuccess) {
    const waMsg = placedOrder ? generateCustomerWhatsAppOrder(placedOrder) : '';
    const waUrl = getWhatsAppLink(waMsg);
    return (
      <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(97,18%,34%)]/10 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[hsl(97,18%,34%)]" />
          </div>
          <h2 className="font-[family-name:var(--font-display)] text-2xl mb-3">{t('order_confirmed')}</h2>
          <p className="text-[hsl(22,10%,38%)] mb-2">{t('order_thank_you')}</p>
          <p className="text-sm text-[hsl(22,10%,38%)] mb-6">{fulfillment === 'pickup' ? t('pay_pickup') : t('pay_delivery')}</p>

          {!whatsAppOpened && (
            <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800 mb-3">
                WhatsApp konnte nicht automatisch geöffnet werden. Bitte klicken Sie den Button unten, um Ihre Bestellung per WhatsApp zu bestätigen.
              </p>
            </div>
          )}

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-whatsapp-confirm"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-[#25D366] text-white shadow-lg hover:brightness-[1.05] transition-colors duration-200"
          >
            {WA_ICON}
            {whatsAppOpened ? 'Bestellung erneut per WhatsApp senden' : 'Bestellung per WhatsApp bestätigen'}
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)]">
      {/* Hero */}
      <section className="bg-[hsl(20,17%,12%)] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl sm:text-4xl lg:text-5xl tracking-[var(--tracking-display)] mb-3">{t('order_title')}</h1>
          <p className="text-white/70 max-w-lg mx-auto">{t('order_subtitle')}</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(97,18%,34%)]/20 border border-[hsl(97,18%,34%)]/30 text-[hsl(97,18%,34%)] text-sm font-medium">
            <span className="w-2 h-2 bg-[hsl(97,18%,34%)] rounded-full animate-pulse" />
            Bis zu 10 % günstiger als bei Lieferando
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {showCheckout && cartItems.length > 0 ? (
            <motion.div key="checkout" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <button onClick={() => setShowCheckout(false)} className="flex items-center gap-2 text-sm text-[hsl(22,10%,38%)] mb-6 hover:text-[hsl(16,55%,36%)] transition-colors duration-150">
                <ArrowLeft className="w-4 h-4" /> {t('back_to_home')}
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Checkout form */}
                <form data-testid="checkout-form" onSubmit={handlePlaceOrder} className="lg:col-span-3 bg-white rounded-2xl border border-[hsl(32,17%,84%)] p-6 sm:p-8 space-y-6">
                  <h2 className="font-[family-name:var(--font-display)] text-xl mb-2">{t('your_order')}</h2>

                  {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                  {/* Fulfillment Toggle */}
                  <div data-testid="checkout-fulfillment-toggle" className="flex gap-2">
                    <button type="button" onClick={() => setFulfillment('pickup')}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${fulfillment === 'pickup' ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-[hsl(36,33%,92%)] text-[hsl(22,10%,38%)]'}`}>
                      <Store className="w-4 h-4" /> {t('pickup')}
                    </button>
                    <button type="button" onClick={() => setFulfillment('delivery')}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${fulfillment === 'delivery' ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-[hsl(36,33%,92%)] text-[hsl(22,10%,38%)]'}`}>
                      <Truck className="w-4 h-4" /> {t('delivery')}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('customer_name')} *</label>
                      <input data-testid="checkout-name-input" type="text" value={orderForm.customer_name}
                        onChange={e => setOrderForm({...orderForm, customer_name: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] focus:ring-2 focus:ring-[hsl(42,72%,44%)] focus:border-transparent outline-none text-sm" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('customer_phone')} *</label>
                      <input data-testid="checkout-phone-input" type="tel" value={orderForm.customer_phone}
                        onChange={e => setOrderForm({...orderForm, customer_phone: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] focus:ring-2 focus:ring-[hsl(42,72%,44%)] focus:border-transparent outline-none text-sm" required />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('customer_email')}</label>
                    <input type="email" value={orderForm.customer_email}
                      onChange={e => setOrderForm({...orderForm, customer_email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] focus:ring-2 focus:ring-[hsl(42,72%,44%)] focus:border-transparent outline-none text-sm" />
                  </div>

                  {fulfillment === 'delivery' && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('delivery_address')} *</label>
                      <input data-testid="checkout-address-input" type="text" value={orderForm.customer_address}
                        onChange={e => setOrderForm({...orderForm, customer_address: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] focus:ring-2 focus:ring-[hsl(42,72%,44%)] focus:border-transparent outline-none text-sm" required />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('notes')}</label>
                    <textarea value={orderForm.notes} onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                      rows={2} className="w-full px-4 py-3 rounded-xl bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] focus:ring-2 focus:ring-[hsl(42,72%,44%)] focus:border-transparent outline-none text-sm resize-none" />
                  </div>

                  <div data-testid="checkout-pay-later-note" className="p-3 rounded-lg bg-[hsl(36,33%,92%)] text-sm text-[hsl(22,10%,38%)]">
                    {fulfillment === 'pickup' ? t('pay_pickup') : t('pay_delivery')}
                  </div>

                  <button type="submit" data-testid="checkout-submit-order-button" disabled={loading}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white shadow-[0_10px_24px_rgba(33,24,16,.10)] hover:brightness-[1.03] transition-colors duration-200 disabled:opacity-50">
                    {loading ? t('loading') : `${t('place_order')} — ${total.toFixed(2)} €`}
                  </button>
                </form>

                {/* Order summary */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-2xl border border-[hsl(32,17%,84%)] p-6 sticky top-32">
                    <h3 className="font-[family-name:var(--font-display)] text-lg mb-4">{t('your_order')} ({itemCount})</h3>
                    <div className="space-y-3 mb-4">
                      {cartItems.map(item => (
                        <div key={item.id} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.name}</p>
                            <p className="text-xs text-[hsl(22,10%,38%)]">{item.quantity}x {item.price.toFixed(2)} €</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-6 h-6 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                            <span className="w-6 text-center text-xs">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-6 h-6 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                            <button onClick={() => removeItem(item.id)} className="w-6 h-6 rounded text-red-500 flex items-center justify-center ml-1"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-[hsl(32,17%,84%)] pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>{t('total')}</span>
                        <span className="text-[hsl(16,55%,36%)]">{total.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div key="browse" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Menu browse */}
                <div className="lg:col-span-2">
                  {/* Category tabs */}
                  <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
                    <button onClick={() => setActiveCategory('all')}
                      className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${activeCategory === 'all' ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-[hsl(36,33%,92%)] text-[hsl(22,10%,38%)]'}`}>
                      {t('all_categories')}
                    </button>
                    {categories.map(cat => (
                      <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                        className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${activeCategory === cat.id ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-[hsl(36,33%,92%)] text-[hsl(22,10%,38%)]'}`}>
                        {getLocalizedField(cat, 'name')}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {filteredItems.map(item => (
                      <div key={item.id} className="bg-white rounded-xl border border-[hsl(32,17%,84%)] p-4 flex items-center gap-4 hover:shadow-sm transition-shadow duration-200">
                        {item.image && <img src={item.image} alt={getLocalizedField(item, 'name')} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm">{getLocalizedField(item, 'name')}</h3>
                          <p className="text-xs text-[hsl(22,10%,38%)] line-clamp-1">{getLocalizedField(item, 'description')}</p>
                        </div>
                        <span className="font-bold text-sm text-[hsl(16,55%,36%)] whitespace-nowrap">{item.price.toFixed(2)} €</span>
                        <button onClick={() => addItem({ id: item.id, name: getLocalizedField(item, 'name'), price: item.price, image: item.image })}
                          className="w-9 h-9 rounded-lg bg-[hsl(16,55%,36%)] text-white flex items-center justify-center flex-shrink-0 hover:brightness-[1.03] transition-colors duration-200">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cart sidebar */}
                <div className="hidden lg:block">
                  <div className="bg-white rounded-2xl border border-[hsl(32,17%,84%)] p-6 sticky top-32">
                    <h3 className="font-[family-name:var(--font-display)] text-lg mb-4 flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5" /> {t('cart')} ({itemCount})
                    </h3>
                    {cartItems.length === 0 ? (
                      <p className="text-sm text-[hsl(22,10%,38%)] text-center py-6">{t('cart_empty')}</p>
                    ) : (
                      <>
                        <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
                          {cartItems.map(item => (
                            <div key={item.id} className="flex items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{item.name}</p>
                                <p className="text-xs text-[hsl(22,10%,38%)]">{item.price.toFixed(2)} €</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-6 h-6 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                <span className="w-5 text-center text-xs">{item.quantity}</span>
                                <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-6 h-6 rounded border border-[hsl(32,17%,84%)] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                                <button onClick={() => removeItem(item.id)} className="w-6 h-6 rounded text-red-500 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-[hsl(32,17%,84%)] pt-3 mb-3">
                          <div className="flex justify-between font-semibold">
                            <span>{t('total')}</span>
                            <span className="text-[hsl(16,55%,36%)]">{total.toFixed(2)} €</span>
                          </div>
                        </div>
                        <button onClick={() => setShowCheckout(true)}
                          className="w-full py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white hover:brightness-[1.03] transition-colors duration-200">
                          {t('checkout')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile floating cart */}
              {cartItems.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-[hsl(32,17%,84%)] p-4 z-40">
                  <button onClick={() => setShowCheckout(true)}
                    className="w-full py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white flex items-center justify-center gap-2">
                    <ShoppingBag className="w-4 h-4" />
                    {t('checkout')} ({itemCount}) — {total.toFixed(2)} €
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
